// client/src/components/pages/LexiStar/FuelConfig/fuelConfig.js
//
// Pure helpers for the Fuel configuration page (ST-00188 M2-A1; LS-03
// FR-FUEL-001 / FR-FUEL-008; LS-04 §9, §11, §12): row ordering, labels,
// form validation and the request bodies for create (POST) and change (PATCH =
// close the row in force, open a new one). Litres are the unit of record (D21);
// rand values are for cost views only and are never shown to drivers.

export const KINDS = {
  norm: { label: 'Consumption norm', plural: 'Consumption norms' },
  price: { label: 'Diesel price', plural: 'Diesel prices' },
  rateCard: { label: 'Rate card', plural: 'Rate cards' },
};

export const STATUS_META = {
  current: { label: 'In force', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-50 text-blue-700 ring-blue-200' },
  superseded: { label: 'Superseded', className: 'bg-slate-100 text-slate-500 ring-slate-200' },
};

const STATUS_ORDER = { current: 0, scheduled: 1, superseded: 2 };

/** A norm's key: what it applies to and in which load state. */
export const normKey = (n) =>
  `${n.scope}|${n.scope === 'asset' ? n.asset_id : n.vehicle_class}|${n.load_state}`;

/** "Class truck" / "JX 12 KL GP" */
export const normSubject = (n) =>
  n.scope === 'asset' ? n.asset_registration || 'Asset' : `Class ${n.vehicle_class}`;

/**
 * Norms ordered for reading: by subject (classes first, then assets), loaded
 * before empty, then in force → scheduled → superseded, newest first.
 */
export function sortNorms(rows) {
  const subject = (n) => `${n.scope === 'asset' ? 1 : 0}|${normSubject(n).toLowerCase()}`;
  return [...rows].sort(
    (a, b) =>
      subject(a).localeCompare(subject(b)) ||
      (a.load_state === b.load_state ? 0 : a.load_state === 'loaded' ? -1 : 1) ||
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
      String(b.effective_from).localeCompare(String(a.effective_from))
  );
}

/** Rows of prices / rate cards ordered by key, then status, newest first. */
export function sortByKey(rows, keyOf) {
  return [...rows].sort(
    (a, b) =>
      String(keyOf(a)).localeCompare(String(keyOf(b))) ||
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
      String(b.effective_from).localeCompare(String(a.effective_from))
  );
}

const isNum = (v) => v !== '' && v != null && Number.isFinite(Number(v));

/** Empty form for a kind; `row` prefills a change. */
export function emptyForm(kind, row = null) {
  if (kind === 'norm') {
    return {
      scope: row?.scope || 'vehicle_class',
      vehicle_class: row?.vehicle_class || 'truck',
      asset_id: row?.asset_id || '',
      load_state: row?.load_state || 'loaded',
      l_per_100km: row?.l_per_100km ?? '',
      idle_lph: row?.idle_lph ?? '',
      route_factor: Object.entries(row?.route_factor || {}).map(([route_id, factor]) => ({
        route_id,
        factor,
      })),
      effective_from: null,
    };
  }
  if (kind === 'price') {
    return {
      region: row?.region || 'ZA',
      price_per_l: row?.price_per_l ?? '',
      effective_from: null,
    };
  }
  return {
    vehicle_class: row?.vehicle_class || 'truck',
    rate_per_km: row?.rate_per_km ?? '',
    driver_rate_per_h: row?.driver_rate_per_h ?? '',
    toll_class: row?.toll_class || '',
    effective_from: null,
  };
}

/** A plain-language problem with the form, or null. */
export function validateForm(kind, mode, form, row = null) {
  if (kind === 'norm') {
    if (mode === 'create' && form.scope === 'vehicle_class' && !String(form.vehicle_class).trim()) {
      return 'Enter the vehicle class.';
    }
    if (mode === 'create' && form.scope === 'asset' && !form.asset_id) return 'Choose the asset.';
    if (
      !isNum(form.l_per_100km) ||
      Number(form.l_per_100km) <= 0 ||
      Number(form.l_per_100km) > 500
    ) {
      return 'Litres per 100 km must be a number above 0 (at most 500).';
    }
    if (form.idle_lph !== '' && form.idle_lph != null) {
      if (!isNum(form.idle_lph) || Number(form.idle_lph) < 0 || Number(form.idle_lph) > 100) {
        return 'Idle litres per hour must be between 0 and 100.';
      }
    }
    for (const f of form.route_factor || []) {
      if (!f.route_id) return 'Choose a route for every route factor, or remove the row.';
      if (!isNum(f.factor) || Number(f.factor) < 0.5 || Number(f.factor) > 3) {
        return 'Route factors must be between 0.5 and 3.';
      }
    }
  } else if (kind === 'price') {
    if (mode === 'create' && !String(form.region).trim()) return 'Enter the region.';
    if (
      !isNum(form.price_per_l) ||
      Number(form.price_per_l) <= 0 ||
      Number(form.price_per_l) > 1000
    ) {
      return 'The price per litre must be a number above 0.';
    }
  } else {
    if (mode === 'create' && !String(form.vehicle_class).trim()) return 'Enter the vehicle class.';
    if (!isNum(form.rate_per_km) || Number(form.rate_per_km) < 0) {
      return 'The rate per km must be a number (0 or more).';
    }
    if (form.driver_rate_per_h !== '' && form.driver_rate_per_h != null) {
      if (!isNum(form.driver_rate_per_h) || Number(form.driver_rate_per_h) < 0) {
        return 'The driver rate per hour must be a number (0 or more).';
      }
    }
  }
  if (mode === 'change' && row && form.effective_from) {
    const at = new Date(form.effective_from).getTime();
    if (at <= new Date(row.effective_from).getTime()) {
      return 'The change must take effect after the current row started.';
    }
  }
  return null;
}

const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
const factorMap = (list) =>
  Object.fromEntries((list || []).map((f) => [f.route_id, Number(f.factor)]));

/** The request body: full key + values for create; changed values for a change. */
export function toPayload(kind, mode, form, row = null) {
  const effective = form.effective_from ? new Date(form.effective_from).toISOString() : undefined;
  if (kind === 'norm') {
    const values = {
      l_per_100km: Number(form.l_per_100km),
      idle_lph: numOrNull(form.idle_lph),
      route_factor: factorMap(form.route_factor),
    };
    if (mode === 'create') {
      return {
        scope: form.scope,
        ...(form.scope === 'asset'
          ? { asset_id: form.asset_id }
          : { vehicle_class: String(form.vehicle_class).trim() }),
        load_state: form.load_state,
        ...values,
        effective_from: effective,
      };
    }
    const out = { effective_from: effective };
    if (Number(row.l_per_100km) !== values.l_per_100km) out.l_per_100km = values.l_per_100km;
    if (numOrNull(row.idle_lph) !== values.idle_lph) out.idle_lph = values.idle_lph;
    if (JSON.stringify(row.route_factor || {}) !== JSON.stringify(values.route_factor)) {
      out.route_factor = values.route_factor;
    }
    return out;
  }
  if (kind === 'price') {
    return mode === 'create'
      ? {
          region: String(form.region).trim() || 'ZA',
          price_per_l: Number(form.price_per_l),
          effective_from: effective,
        }
      : { price_per_l: Number(form.price_per_l), effective_from: effective };
  }
  const values = {
    rate_per_km: Number(form.rate_per_km),
    driver_rate_per_h: numOrNull(form.driver_rate_per_h),
    toll_class: String(form.toll_class || '').trim() || null,
  };
  if (mode === 'create') {
    return {
      vehicle_class: String(form.vehicle_class).trim(),
      ...values,
      effective_from: effective,
    };
  }
  const out = { effective_from: effective };
  if (Number(row.rate_per_km) !== values.rate_per_km) out.rate_per_km = values.rate_per_km;
  if (numOrNull(row.driver_rate_per_h) !== values.driver_rate_per_h) {
    out.driver_rate_per_h = values.driver_rate_per_h;
  }
  if ((row.toll_class || null) !== values.toll_class) out.toll_class = values.toll_class;
  return out;
}

/** Whether a change payload changes a value (not just the date). */
export const changesSomething = (payload) =>
  Object.keys(payload).some((k) => k !== 'effective_from');

/** "R 23.450" / "—" */
export function formatRand(v, decimals = 2) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  return `R ${Number(v).toFixed(decimals)}`;
}
